import { useState, useEffect } from 'react';
import { PublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { msalConfig, loginRequest } from '../config/msalConfig';
import { AuthService } from '../services/auth';
import { logger } from '../utils/logger';

export const useSSO = () => {
  const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeMSAL = async () => {
      try {
        // Verificar se as variáveis de ambiente estão configuradas
        const clientId = import.meta.env.VITE_AZURE_CLIENT_ID;
        const tenantId = import.meta.env.VITE_AZURE_TENANT_ID;

        if (!clientId || !tenantId) {
          logger.warn('Variáveis de ambiente do Azure não configuradas. SSO desabilitado.');
          setIsLoading(false);
          return;
        }

        // Verificar se já existe uma instância MSAL no window
        if (window.msalInstance) {
          logger.debug('Usando instância MSAL existente');
          setMsalInstance(window.msalInstance);
          AuthService.initializeMSAL(window.msalInstance);
          AuthService.setSSOMode(true);
          setIsLoading(false);
          return;
        }

        // Verificar se já existe uma instância MSAL no AuthService
        if (AuthService.msalInstance) {
          logger.debug('Usando instância MSAL do AuthService');
          setMsalInstance(AuthService.msalInstance);
          AuthService.setSSOMode(true);
          setIsLoading(false);
          return;
        }

        // Criar nova instância do MSAL apenas se não existir nenhuma
        logger.info('Criando nova instância MSAL...');
        const msal = new PublicClientApplication(msalConfig);
        await msal.initialize();

        // Armazenar instância globalmente para evitar duplicação
        window.msalInstance = msal;

        // Inicializar serviço de autenticação
        AuthService.initializeMSAL(msal);
        AuthService.setSSOMode(true);

        setMsalInstance(msal);
        setError(null);
        logger.success('MSAL inicializado com sucesso');
      } catch (err) {
        logger.error('Erro ao inicializar MSAL:', err);
        setError('Erro ao configurar autenticação Microsoft');
        AuthService.setSSOMode(false);
      } finally {
        setIsLoading(false);
      }
    };

    initializeMSAL();
  }, []);

  const loginWithMicrosoft = async () => {
    if (!msalInstance) {
      throw new Error('MSAL não inicializado');
    }

    try {
      setIsLoading(true);
      setError(null);

      const result = await AuthService.loginWithMicrosoft();
      
      if (!result.success) {
        setError(result.error || 'Erro no login');
        return { success: false, error: result.error };
      }

      return { success: true, user: result.user };
    } catch (err) {
      const errorMessage = 'Erro na autenticação Microsoft';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await AuthService.logout();
    } catch (err) {
      console.error('Erro no logout:', err);
    }
  };

  const isSSOAvailable = () => {
    return msalInstance !== null && 
           import.meta.env.VITE_AZURE_CLIENT_ID && 
           import.meta.env.VITE_AZURE_TENANT_ID;
  };

  return {
    msalInstance,
    isLoading,
    error,
    loginWithMicrosoft,
    logout,
    isSSOAvailable: isSSOAvailable()
  };
};
